/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLinkIcon, FileTextIcon, TrashIcon, UploadIcon } from '@radix-ui/react-icons';

import {
  ActionIcon,
  Button,
  CardListSkeleton,
  DataState,
  NativeFileInput,
  Select,
  Text,
  TextInput,
} from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

import { MicroLabel, PanelEmptyState, SurfaceCard } from './sale-detail-atoms';

/** Matches the backend's own cap, so the field cannot compose a refusal. */
const MAX_LABEL_LENGTH = 120;

export type EntityDocument = {
  document_id: string;
  filename: string;
  category: string;
  /** Free-text name, for a document the vocabulary has no word for. */
  label?: string | null;
  uploaded_at?: string;
};

export type EntityDocumentsPanelProps = {
  documents: EntityDocument[];
  categories: readonly string[];
  /**
   * Labels for categories the i18n bundle has no key for — the ones an
   * agency added itself. Built-in categories are left to `t()`, so they
   * stay translated in every locale.
   */
  categoryLabels?: Record<string, string>;
  /** Namespace holding `documents.*` labels for this record type. */
  namespace: string;
  /**
   * The catch-all category, the one that takes a free-text name. Only this
   * one asks for a name: the other six already say what the file is.
   */
  freeLabelCategory?: string;
  isLoading?: boolean;
  isError?: boolean;
  onUpload: (file: File, category: string, label?: string) => Promise<void>;
  onOpen: (documentId: string) => Promise<void>;
  onDelete: (documentId: string) => Promise<void>;
  onRetry?: () => void;
};

/**
 * Documents attached to a record — one panel serving both the Sale and the
 * Lease, because the paperwork behaves identically even though the category
 * vocabularies differ. The categories are passed in rather than imported, so
 * neither feature has to know about the other.
 *
 * Same card chrome and dashed-upload-button idiom as the attendance detail
 * screen's documents tab. The category/label pair above the list is this
 * panel's own addition — the attendance side has no such vocabulary to pick
 * from, so there is nothing there to match.
 *
 * @param props - Documents, the record type's categories, and the actions.
 * @returns The rendered documents panel.
 */
export function EntityDocumentsPanel({
  documents,
  categories,
  categoryLabels,
  namespace,
  freeLabelCategory = 'other',
  isLoading = false,
  isError = false,
  onUpload,
  onOpen,
  onDelete,
  onRetry,
}: EntityDocumentsPanelProps) {
  const { t } = useTranslation(namespace);
  const fileRef = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState<string>(categories[0] ?? 'outros');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);

  const asksForLabel = category === freeLabelCategory;

  /** The agency's own name for a category, else the translated built-in. */
  const categoryLabel = (id: string) =>
    categoryLabels?.[id] ?? t(`documents.categories.${id}`, { defaultValue: id });

  async function handlePicked(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    try {
      await onUpload(file, category, asksForLabel ? label.trim() || undefined : undefined);
      setLabel('');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <SurfaceCard testId="entity-documents-panel" className="gap-(--space-4)">
      <div className="flex flex-wrap items-center justify-between gap-(--space-2)">
        <MicroLabel>{t('documents.title')}</MicroLabel>
        <div className="flex items-center gap-(--space-2)">
          <Select
            aria-label={t('documents.category')}
            data-testid="document-category-select"
            value={category}
            onChange={(value) => setCategory((value as string) ?? category)}
            data={categories.map((c) => ({ value: c, label: categoryLabel(c) }))}
          />
          {/* Only the catch-all asks for a name: the other categories
              already say what the file is, and an always-visible field
              would read as required when it never is. */}
          {asksForLabel && (
            <TextInput
              aria-label={t('documents.labelField')}
              data-testid="document-label-input"
              placeholder={t('documents.labelPlaceholder')}
              maxLength={MAX_LABEL_LENGTH}
              value={label}
              onChange={(event) => setLabel(event.target.value)}
            />
          )}
        </div>
      </div>

      <DataState
        isLoading={isLoading}
        isError={isError}
        isEmpty={documents.length === 0}
        loadingFallback={<CardListSkeleton count={2} />}
        onRetry={onRetry}
        emptyState={
          <PanelEmptyState
            message={t('documents.emptyTitle')}
            hint={t('documents.emptyDescription')}
          />
        }
      >
        <div className="flex flex-col gap-(--space-2)">
          {documents.map((doc) => (
            <DocumentRow
              key={doc.document_id}
              doc={doc}
              namespace={namespace}
              categoryLabel={categoryLabel}
              onOpen={() => void onOpen(doc.document_id)}
              onDelete={() => void onDelete(doc.document_id)}
            />
          ))}
        </div>
      </DataState>

      <NativeFileInput
        ref={fileRef}
        style={{ display: 'none' }}
        data-testid="document-file-input"
        onChange={(e) => void handlePicked(e.target.files?.[0])}
      />
      <Button
        disabled={busy}
        data-testid="document-upload-button"
        onClick={() => fileRef.current?.click()}
        className={cn(
          'inline-flex items-center gap-(--space-2) self-start px-(--space-3) py-(--space-2)',
          'text-[length:var(--text-meta)] font-[weight:var(--font-medium)]',
          'rounded-(--radius-md) border border-dashed border-(--seed-base)',
          'bg-transparent text-(--seed-base) cursor-pointer',
          'hover:bg-[color-mix(in_srgb,var(--seed-base)_5%,var(--surface-primary))]',
          'transition-[var(--transition-standard)]',
        )}
      >
        <UploadIcon width={13} height={13} />
        {busy ? t('documents.uploading') : t('documents.upload')}
      </Button>
    </SurfaceCard>
  );
}

/**
 * One attached file: icon, name, category or free-text label, and — on
 * hover or focus — open/remove actions. Mirrors the attendance detail
 * screen's document row, minus rename: neither Sale nor Lease documents
 * carry that capability today.
 *
 * @param props.doc - The document record.
 * @param props.namespace - i18n namespace for this record type.
 * @param props.categoryLabel - Resolves a category id to its display name.
 * @param props.onOpen - Opens the document in a new tab.
 * @param props.onDelete - Removes the document.
 * @returns The rendered row.
 */
function DocumentRow({
  doc,
  namespace,
  categoryLabel,
  onOpen,
  onDelete,
}: {
  doc: EntityDocument;
  namespace: string;
  categoryLabel: (id: string) => string;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation(namespace);

  return (
    <div
      data-testid="document-row"
      className={cn(
        'group flex w-full items-center gap-(--space-2) px-(--space-3) py-(--space-2)',
        'rounded-(--radius-md) border border-(--border-ultra-subtle) bg-(--surface-primary)',
        'hover:border-(--border-subtle) hover:shadow-[var(--shadow-sm)]',
        'transition-[var(--transition-standard)]',
      )}
    >
      <FileTextIcon width={14} height={14} className="shrink-0 text-(--seed-base)" />
      <div className="flex min-w-0 flex-1 flex-col">
        <Text component="span" size="sm" fw={500} className="truncate text-(--text-primary)">
          {doc.filename}
        </Text>
        <Text component="span" size="xs" className="text-(--text-secondary)">
          {/* The name the broker gave it wins over the category:
              "Outros" is what the vocabulary lacked, not what the
              document is. */}
          {doc.label || categoryLabel(doc.category)}
        </Text>
      </div>
      <div className="flex shrink-0 items-center gap-(--space-1) opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
        <ActionIcon
          type="button"
          aria-label={t('documents.open')}
          title={t('documents.open')}
          onClick={onOpen}
          size={28}
          className="text-(--text-secondary) hover:text-(--seed-base)"
        >
          <ExternalLinkIcon width={14} height={14} />
        </ActionIcon>
        <ActionIcon
          type="button"
          aria-label={t('documents.remove')}
          title={t('documents.remove')}
          onClick={onDelete}
          size={28}
          className="text-(--text-secondary) hover:text-(--status-error)"
        >
          <TrashIcon width={14} height={14} />
        </ActionIcon>
      </div>
    </div>
  );
}

export default EntityDocumentsPanel;
